//Helpers
import { dateFormat } from 'src/helpers/date_helper';
import { deepCloneObj } from '../../core/chart_helper';
import { convertToUnit, convertToValueAndUnit } from 'src/helpers/units_helpers';
import CacheStatisticRequest from '../../request/chart_cache_statistics_helpers';
//Types
import type {
	GetStatsResponseData,
	GetStatsResponseHistoryData,
	RequestStatisticData,
} from 'src/types/requests/charts/get_stats_period';
import type { ChartConfigSeriesData, ConsumptionDataset } from 'src/types/Chart';

export const getNetMetering = async (
	reqURL: string,
	reqData: RequestStatisticData,
	resData: GetStatsResponseData,
	server: string,
	isThreeEMTriphase: boolean = false,
): Promise<GetStatsResponseData> => {
	const netMeteringRes = await CacheStatisticRequest.getData(reqURL, reqData, server);
	if (!netMeteringRes?.error && netMeteringRes?.history) {
		const netMeteringResData: GetStatsResponseData = netMeteringRes?.history
			? netMeteringRes
			: netMeteringRes.data;
		resData = addNetMetering(resData, netMeteringResData, isThreeEMTriphase);
	}
	return resData;
};

export const calculateNetMetering = (consumptionDataset: ConsumptionDataset) => {
	const dataset = Object.keys(consumptionDataset);
	if (!dataset?.length) return consumptionDataset;

	for (const key of dataset) {
		const item: ChartConfigSeriesData = consumptionDataset[key];
		if (item?.missing || item?.active_net == null) continue;

		const netValue = item.active_net;
		if (item.reversed) {
			item.value = netValue < 0 ? netValue : 0;
			continue;
		}

		const originalConsumption = item.value;
		item.value = Math.max(netValue, 0);
		if (originalConsumption > 0) {
			const costPerUnit = (1000 / originalConsumption) * (item.cost || 0);
			item.cost = (item.value / 1000) * costPerUnit;
			for (const meta of item?.metaData || []) {
				if (!meta?.tariff) continue;
				const scaleRatio = item.value / originalConsumption;
				const scaledValue = meta.tariff.value * scaleRatio;
				const valueAndUnit = convertToValueAndUnit(scaledValue, meta.tariff.unit);
				meta.tariff.value = valueAndUnit.value as number;
				meta.value = `${valueAndUnit.value} ${valueAndUnit.unit}`;
				meta.tariff.cost = (scaledValue / 1000) * costPerUnit;
				meta.cost = convertToUnit(meta.tariff.cost, meta.tariff.currency, {
					fixed: 2,
					dontConvert: true,
				});
			}
		}
	}
	return consumptionDataset;
};

const addNetMetering = (
	resData: GetStatsResponseData,
	netMeteringResData: GetStatsResponseData,
	isThreeEMTriphase: boolean = false,
): GetStatsResponseData => {
	if (!netMeteringResData?.history?.length) {
		return { history: [] };
	}
	resData = deepCloneObj(resData);
	if (isThreeEMTriphase) {
		for (let i = 0; i < resData.history.length; i++) {
			//@ts-ignore
			resData.history[i] = addNetMeteringByChannel(
				//@ts-ignore
				resData.history[i],
				netMeteringResData.history[i],
			);
		}
		resData.sum = addNetMeteringByChannel(resData.sum, netMeteringResData.sum);
	} else {
		resData.history = addNetMeteringByChannel(resData.history, netMeteringResData.history);
	}
	return resData;
};

const addNetMeteringByChannel = (
	resHistory: GetStatsResponseHistoryData[],
	netMeteringHistory: GetStatsResponseHistoryData[],
) => {
	const dateFormating = 'Y-m-dTH:i:s';
	for (let i = 0; i < resHistory.length; i++) {
		const historyItem: GetStatsResponseHistoryData = resHistory[i];
		const datetime: string = dateFormat(historyItem.datetime, dateFormating);
		if (netMeteringHistory?.find && !historyItem?.missing) {
			const netMeteringItem: GetStatsResponseHistoryData = netMeteringHistory.find(
				(i) => dateFormat(i.datetime, dateFormating) == datetime,
			);
			if (!netMeteringItem) {
				resHistory[i] = { datetime: resHistory[i].datetime, missing: true };
				continue;
			}
			resHistory[i].active_net = netMeteringItem?.active_net || 0;
		}
	}
	return resHistory;
};
